import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="orders--shipping-addresses"
export default class extends Controller {
  static values = {
    addressesUrl: String,
    processing: String,
    saveError: String,
  };

  static targets = [
    "form",
    "errors",
    "shippingAddressSelect",
    "shippingFirstName",
    "shippingLastName",
    "shippingCompany",
    "shippingVatNumber",
    "shippingStreet",
    "shippingStreetInformation",
    "shippingZipcode",
    "shippingCity",
    "shippingState",
    "shippingCountry",
    "shippingPhone",
    "billingAddressSelect",
    "billingFirstName",
    "billingLastName",
    "billingCompany",
    "billingSiren",
    "billingVatNumber",
    "billingStreet",
    "billingStreetInformation",
    "billingZipcode",
    "billingCity",
    "billingState",
    "billingCountry",
    "billingPhone",
  ];

  update() {
    const formData = new FormData(this.formTarget);

    this.pendingUpdate = (this.pendingUpdate || Promise.resolve()).then(async () => {
      const response = await fetch(this.formTarget.action, {
        method: this.formTarget.method,
        headers: { Accept: "application/json" },
        body: formData,
      });

      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        this.showErrors(result.errors?.length ? result.errors : [this.saveErrorValue]);
        return false;
      }

      this.clearErrors();
      return true;
    }).catch((error) => {
      console.error("shipping_addresses_controller: form update failed", error);
      this.showErrors([this.saveErrorValue]);
      return false;
    });

    return this.pendingUpdate;
  }

  setBillingAddress() {
    const id = this.billingAddressSelectTarget.value;

    if (id) {
      this.billingAddressLoad = fetch(`${this.addressesUrlValue}/${id}.json`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
        .then((response) => {
          if (!response.ok) throw new Error("billing address load failed");
          return response.json();
        })
        .then((address) => {
          if (this.billingAddressSelectTarget.value !== id) return true;

          this.billingFirstNameTarget.value = address.first_name;
          this.billingLastNameTarget.value = address.last_name;
          this.billingCompanyTarget.value = address.company;
          this.billingSirenTarget.value = address.siren || "";
          this.billingVatNumberTarget.value = address.vat_number;
          this.billingStreetTarget.value = address.street;
          this.billingStreetInformationTarget.value =
            address.street_information;
          this.billingZipcodeTarget.value = address.zipcode;
          this.billingCityTarget.value = address.city;
          this.billingStateTarget.value = address.state;
          this.billingCountryTarget.value = address.country;
          this.billingPhoneTarget.value = address.phone;

          return this.update();
        }).catch((error) => {
          console.error("shipping_addresses_controller: billing address load failed", error);
          this.showErrors([this.saveErrorValue]);
          return false;
        });
    } else {
      this.billingAddressLoad = Promise.resolve(true);
    }
  }

  setShippingAddress() {
    const id = this.shippingAddressSelectTarget.value;

    if (id) {
      this.shippingAddressLoad = fetch(`${this.addressesUrlValue}/${id}.json`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
        .then((response) => {
          if (!response.ok) throw new Error("shipping address load failed");
          return response.json();
        })
        .then((address) => {
          if (this.shippingAddressSelectTarget.value !== id) return true;

          this.shippingFirstNameTarget.value = address.first_name;
          this.shippingLastNameTarget.value = address.last_name;
          this.shippingCompanyTarget.value = address.company;
          // this.shippingVatNumberTarget.value = address.vat_number;
          this.shippingStreetTarget.value = address.street;
          this.shippingStreetInformationTarget.value =
            address.street_information;
          this.shippingZipcodeTarget.value = address.zipcode;
          this.shippingCityTarget.value = address.city;
          this.shippingStateTarget.value = address.state;
          this.shippingCountryTarget.value = address.country;
          this.shippingPhoneTarget.value = address.phone;

          return this.update();
        }).catch((error) => {
          console.error("shipping_addresses_controller: shipping address load failed", error);
          this.showErrors([this.saveErrorValue]);
          return false;
        });
    } else {
      this.shippingAddressLoad = Promise.resolve(true);
    }
  }

  async useShippingAddress(event) {
    event.preventDefault();
    const submitter = event.currentTarget;
    const loaded = await Promise.all([this.shippingAddressLoad, this.billingAddressLoad]);
    if (loaded.includes(false)) return;
    await this.pendingUpdate;

    this.billingFirstNameTarget.value = this.shippingFirstNameTarget.value;
    this.billingLastNameTarget.value = this.shippingLastNameTarget.value;
    this.billingCompanyTarget.value = this.shippingCompanyTarget.value;
    this.billingSirenTarget.value = "";
    this.billingVatNumberTarget.value = "";
    this.billingStreetTarget.value = this.shippingStreetTarget.value;
    this.billingStreetInformationTarget.value =
      this.shippingStreetInformationTarget.value;
    this.billingZipcodeTarget.value = this.shippingZipcodeTarget.value;
    this.billingCityTarget.value = this.shippingCityTarget.value;
    this.billingStateTarget.value = this.shippingStateTarget.value;
    this.billingCountryTarget.value = this.shippingCountryTarget.value;
    this.billingPhoneTarget.value = this.shippingPhoneTarget.value;
    this.formTarget.requestSubmit(submitter);
  }

  async startPayment(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const label = button.textContent;
    button.disabled = true;
    button.textContent = this.processingValue;

    const loaded = await Promise.all([this.billingAddressLoad, this.shippingAddressLoad]);
    if (!loaded.includes(false) && await this.update()) {
      button.form.requestSubmit();
    } else {
      button.disabled = false;
      button.textContent = label;
    }
  }

  showErrors(errors) {
    this.errorsTarget.textContent = errors.join(" ");
    this.errorsTarget.classList.remove("hidden");
  }

  clearErrors() {
    this.errorsTarget.textContent = "";
    this.errorsTarget.classList.add("hidden");
  }
}
